import { callBridgeTool, listBridgeTools, type McpToolCallResponse, type McpToolContent } from '../mcp/bridgeClient'

export type AssistantTool = {
  name: string
  bridgeName?: string
  description: string
  argsSchema: Record<string, unknown>
}

const QUERY_SCHEMA = {
  type: 'string',
  optional: true,
  description: 'Short user-facing summary for this tool call.',
}

const READ_ONLY_TOOLS: AssistantTool[] = [
  {
    name: 'list-entity-types',
    description: 'List entity types (read-only)',
    argsSchema: {
      query: QUERY_SCHEMA,
      pageIndex: { type: 'number', optional: true },
      pageSize: { type: 'number', optional: true },
    },
  },
  {
    name: 'get-entity-type',
    description: 'Get an entity type by id (read-only)',
    argsSchema: { query: QUERY_SCHEMA, id: { type: 'string' } },
  },
  {
    name: 'list-entities',
    description: 'List entities of a type (read-only)',
    argsSchema: {
      query: QUERY_SCHEMA,
      typeId: { type: 'string' },
      pageIndex: { type: 'number', optional: true },
      pageSize: { type: 'number', optional: true },
      related: { type: 'boolean', optional: true },
      search: { type: 'string', optional: true, description: 'Search string to filter entities' },
      assetPayload: { type: 'boolean', optional: true, description: 'Include asset Payload field values' },
      assetThumbnail: { type: 'boolean', optional: true, description: 'Include asset Thumbnail field values' },
    },
  },
  {
    name: 'get-entity',
    description: 'Get an entity by id (read-only)',
    argsSchema: {
      query: QUERY_SCHEMA,
      id: { type: 'string' },
      assetPayload: { type: 'boolean', optional: true, description: 'Include asset Payload field values' },
      assetThumbnail: { type: 'boolean', optional: true, description: 'Include asset Thumbnail field values' },
    },
  },
]

const isToolAvailable = (name: string, available: readonly { name: string }[]) =>
  available.some((tool) => tool.name === name)

const resolveBridgeName = (name: string) => READ_ONLY_TOOLS.find((tool) => tool.name === name)?.bridgeName ?? name

export const checkMcpToolsReady = async (): Promise<{ ready: boolean; availableTools: string[]; error?: string }> => {
  try {
    const { tools } = await listBridgeTools()
    const missing = READ_ONLY_TOOLS.filter((tool) => !isToolAvailable(resolveBridgeName(tool.name), tools)).map(
      (tool) => tool.name
    )
    const availableNames = READ_ONLY_TOOLS.filter((tool) => isToolAvailable(resolveBridgeName(tool.name), tools)).map(
      (tool) => tool.name
    )
    return {
      ready: missing.length === 0,
      availableTools: availableNames,
      error: missing.join(', ') || undefined,
    }
  } catch (error) {
    return { ready: false, availableTools: [], error: error instanceof Error ? error.message : String(error) }
  }
}

export const getAssistantTools = (): AssistantTool[] => READ_ONLY_TOOLS

export type AssistantToolCallResult = {
  success: boolean
  content?: McpToolContent[]
  error?: string
}

export const callAssistantTool = async (
  name: string,
  args: Record<string, unknown>
): Promise<AssistantToolCallResult> => {
  try {
    const response: McpToolCallResponse = await callBridgeTool(resolveBridgeName(name), args)
    return { success: true, content: response.content }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) }
  }
}
